import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardBody, CardHeader, Col, Container, Form, Row } from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import type { RootState } from '../../slices';
import {
    HeaderSection,
    CarrierSection,
    TransferSection,
    DestinationSection,
    SustentoSection,
    ProductDetailsSection,
    AdditionalFieldsSection,
    ActionButtons
} from './Components';
import useRemissionGuide from './Components/useRemissionGuide';
import FullScreenLoader from "../../Components/Common/FullScreenLoader";
import GenericSuccessModal, { createRemissionGuideSuccessConfig } from "../../Components/Common/SuccessModal";
import ConfirmModal, { createRemissionGuideConfirmData } from '../Billing/Components/ConfirmModal';
import { useBlockSpecialChars } from "../../Components/Common/useBlockSpecialChars";

const RemissionGuide: React.FC = () => {
    useBlockSpecialChars();
    const companies = useSelector((state: RootState) => state.Companies.companyList);
    const establishments = useSelector((state: RootState) => state.Companies.establishmentList);

    const [loadingCopy, setLoadingCopy] = useState<boolean>(() => {
        try {
            return !!sessionStorage.getItem('copiedRemissionData');
        } catch {
            return false;
        }
    });

    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const handleShowConfirmModal = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmSubmit = () => {
        setShowConfirmModal(false);
        formik.handleSubmit();
    };

    const handleCancelConfirm = () => {
        setShowConfirmModal(false);
    };

    const handleNewGuide = () => {
        setShowSuccessModal(false);
        formik.resetForm();
        setDetails([]);
        setAdditionalFields([]);
    };

    const handleGoToGuideList = () => {
        setShowSuccessModal(false);
        window.location.href = '/remission-guide-list';
    };

    const {
        selectedCompany,
        selectedEstablishment,
        selectedEmissionPoint,
        details,
        additionalFields,
        jwtPayload,
        isLoading,
        isSubmitting,
        showSuccessModal,
        documentTypes,
        formik,
        addEmptyDetail,
        removeDetail,
        updateDetail,
        addEmptyAdditionalField,
        removeAdditionalField,
        updateAdditionalField,
        handleCompanyChange,
        handleEstablishmentChange,
        handleEmissionPointChange,
        setDetails,
        setAdditionalFields,
        setShowSuccessModal,
    } = useRemissionGuide();

    const handleValidateSustento = () => {
    };

    useEffect(() => {
        const hasCopied = !!sessionStorage.getItem('copiedRemissionData');
        if (hasCopied && documentTypes.length === 0) {
            setLoadingCopy(true);
        } else {
            setLoadingCopy(false);
        }
    }, [documentTypes.length]);

    document.title = 'Guía de Remisión | Vuelo';

    if (isLoading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <h5 className="text-muted">Inicializando desde token de autenticación...</h5>
                            <p className="text-muted">Cargando información del emisor</p>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content position-relative">
            <BreadCrumb title="Nueva Guía de Remisión" pageTitle="Dashboard" />
            <Container fluid>
                {loadingCopy && (
                    <FullScreenLoader
                        title="Preparando documento"
                        message="Estamos configurando los detalles y catálogos necesarios para duplicar su guía de remisión. Esto tomará solo un momento."
                        iconType="copy"
                        showProgress={true}
                    />
                )}

                {formik.isSubmitting && (
                    <FullScreenLoader
                        title="Procesando Guía de Remisión"
                        message="Estamos generando y enviando su guía de remisión al SRI. Esto tomará solo un momento."
                        showProgress={true}
                        iconType="printer"
                    />
                )}
                {!loadingCopy && !isSubmitting && (
                    <Row>
                        <Col lg={12}>
                            <Card className="shadow-lg border-0 rounded-3">
                                <CardHeader className="bg-soft-light border-bottom p-4">
                                    <div className="d-flex align-items-center">
                                        <div className="avatar-sm bg-white shadow-sm rounded-circle p-2 me-3 d-flex align-items-center justify-content-center">
                                            <i className="ri-truck-line text-primary h2 m-0"></i>
                                        </div>
                                        <div>
                                            <h4 className="card-title mb-1 text-primary fw-bold">Nueva Guía de Remisión</h4>
                                            <p className="text-muted mb-0">Complete la información para generar el documento electrónico</p>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody className="p-4 bg-light">
                                    <Form onSubmit={formik.handleSubmit}>

                                        <div className="mb-4">
                                            <HeaderSection
                                                selectedCompany={selectedCompany}
                                                selectedEstablishment={selectedEstablishment}
                                                selectedEmissionPoint={selectedEmissionPoint}
                                                jwtPayload={jwtPayload}
                                                onCompanyChange={handleCompanyChange}
                                                onEstablishmentChange={handleEstablishmentChange}
                                                onEmissionPointChange={handleEmissionPointChange}
                                                companies={companies}
                                                establishments={establishments}
                                            />
                                        </div>

                                        <Row className="g-4 mb-4">
                                            <Col lg={6}>
                                                <div className="h-100">
                                                    <CarrierSection
                                                        values={{
                                                            carrierIdType: formik.values.carrierIdType,
                                                            carrierId: formik.values.carrierId,
                                                            carrierName: formik.values.carrierName,
                                                            carrierEmail: formik.values.carrierEmail,
                                                        }}
                                                        touched={{
                                                            carrierIdType: formik.touched.carrierIdType,
                                                            carrierId: formik.touched.carrierId,
                                                            carrierName: formik.touched.carrierName,
                                                            carrierEmail: formik.touched.carrierEmail,
                                                        }}
                                                        errors={{
                                                            carrierIdType: formik.errors.carrierIdType,
                                                            carrierId: formik.errors.carrierId,
                                                            carrierName: formik.errors.carrierName,
                                                            carrierEmail: formik.errors.carrierEmail,
                                                        }}
                                                        onChange={(field, value) => formik.setFieldValue(field, value)}
                                                        formik={formik}
                                                        documentTypes={documentTypes}
                                                    />
                                                </div>
                                            </Col>
                                            <Col lg={6}>
                                                <div className="h-100">
                                                    <TransferSection
                                                        values={{
                                                            startingAddress: formik.values.startingAddress,
                                                            startDate: formik.values.startDate,
                                                            endDate: formik.values.endDate,
                                                            plate: formik.values.plate,
                                                        }}
                                                        touched={{
                                                            startingAddress: formik.touched.startingAddress,
                                                            startDate: formik.touched.startDate,
                                                            endDate: formik.touched.endDate,
                                                            plate: formik.touched.plate,
                                                        }}
                                                        errors={{
                                                            startingAddress: formik.errors.startingAddress,
                                                            startDate: formik.errors.startDate,
                                                            endDate: formik.errors.endDate,
                                                            plate: formik.errors.plate,
                                                        }}
                                                        onChange={(field, value) => formik.setFieldValue(field, value)}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row className="g-4 mb-4">
                                            <Col lg={6}>
                                                <div className="h-100">
                                                    <DestinationSection
                                                        values={{
                                                            destinatarioIdType: formik.values.destinatarioIdType,
                                                            destinatarioId: formik.values.destinatarioId,
                                                            destinatarioName: formik.values.destinatarioName,
                                                            arrivalAddress: formik.values.arrivalAddress,
                                                            route: formik.values.route,
                                                            reasonForTransfer: formik.values.reasonForTransfer,
                                                            customsDoc: formik.values.customsDoc,
                                                        }}
                                                        touched={{
                                                            destinatarioIdType: formik.touched.destinatarioIdType,
                                                            destinatarioId: formik.touched.destinatarioId,
                                                            destinatarioName: formik.touched.destinatarioName,
                                                            arrivalAddress: formik.touched.arrivalAddress,
                                                            route: formik.touched.route,
                                                            reasonForTransfer: formik.touched.reasonForTransfer,
                                                            customsDoc: formik.touched.customsDoc,
                                                        }}
                                                        errors={{
                                                            destinatarioIdType: formik.errors.destinatarioIdType,
                                                            destinatarioId: formik.errors.destinatarioId,
                                                            destinatarioName: formik.errors.destinatarioName,
                                                            arrivalAddress: formik.errors.arrivalAddress,
                                                            route: formik.errors.route,
                                                            reasonForTransfer: formik.errors.reasonForTransfer,
                                                            customsDoc: formik.errors.customsDoc,
                                                        }}
                                                        onChange={(field, value) => formik.setFieldValue(field, value)}
                                                        formik={formik}
                                                        documentTypes={documentTypes}
                                                    />
                                                </div>
                                            </Col>
                                            <Col lg={6}>
                                                <div className="h-100">
                                                    <SustentoSection
                                                        values={{
                                                            emissionType: formik.values.emissionType,
                                                            documentType: formik.values.documentType,
                                                            authNumber: formik.values.authNumber,
                                                            sustentoDate: formik.values.sustentoDate,
                                                            sustentoNum: formik.values.sustentoNum,
                                                        }}
                                                        touched={{
                                                            emissionType: formik.touched.emissionType,
                                                            documentType: formik.touched.documentType,
                                                            authNumber: formik.touched.authNumber,
                                                            sustentoDate: formik.touched.sustentoDate,
                                                            sustentoNum: formik.touched.sustentoNum,
                                                        }}
                                                        errors={{
                                                            emissionType: formik.errors.emissionType,
                                                            documentType: formik.errors.documentType,
                                                            authNumber: formik.errors.authNumber,
                                                            sustentoDate: formik.errors.sustentoDate,
                                                            sustentoNum: formik.errors.sustentoNum,
                                                        }}
                                                        onChange={(field, value) => formik.setFieldValue(field, value)}
                                                        onValidate={handleValidateSustento}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>

                                        <div className="mb-4">
                                            <ProductDetailsSection
                                                details={details}
                                                onAddDetail={addEmptyDetail}
                                                onUpdateDetail={updateDetail}
                                                onRemoveDetail={removeDetail}
                                            />
                                        </div>

                                        <Row>
                                            <Col lg={6} md={12}>
                                                <AdditionalFieldsSection
                                                    additionalFields={additionalFields}
                                                    onAddField={addEmptyAdditionalField}
                                                    onUpdateField={updateAdditionalField}
                                                    onRemoveField={removeAdditionalField}
                                                />
                                            </Col>
                                        </Row>
                                        <div>
                                            <ActionButtons
                                                onSubmit={handleShowConfirmModal}
                                                disabled={details.length === 0}
                                                loading={isSubmitting}
                                                formik={formik}
                                            />
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>

            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={handleCancelConfirm}
                onConfirm={handleConfirmSubmit}
                data={createRemissionGuideConfirmData(formik.values, details.length)}
                isSubmitting={isSubmitting}
            />

            <GenericSuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                onPrimaryAction={handleNewGuide}
                onSecondaryAction={handleGoToGuideList}
                config={createRemissionGuideSuccessConfig()}
            />
        </div>
    );
};

export default RemissionGuide;
